import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-4">
            <h1 className="text-5xl font-bold mb-2">PCAB Reporting System</h1>
            <p className="text-xl mb-4">Philippine Contractors Accreditation Board</p>
          </div>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Your trusted platform for reporting contractor violations, construction project issues,
            and ensuring quality standards in the Philippine construction industry
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/report" className="border-2 border-white text-white hover:bg-white hover:text-blue-800 text-lg px-8 py-3 rounded transition-colors">
              Submit a Report
            </Link>

          </div>
        </div>
      </section>

      {/* About PCAB Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">About PCAB</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              The Philippine Contractors Accreditation Board (PCAB) is the government agency responsible for
              accrediting construction contractors and ensuring compliance with quality standards in the
              Philippine construction industry. Our reporting system helps maintain transparency and
              accountability in construction projects nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-primary text-center mb-12">How the Reporting Process Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-4">Submit Your Report</h3>
              <p className="text-gray-700">
                Complete our secure online form with details about contractor violations, safety issues,
                or project concerns. Include project location, contractor information, and upload supporting
                documents such as photos, contracts, or permits. Reports can be submitted anonymously.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-4">Verification & Review</h3>
              <p className="text-gray-700">
                PCAB officials review your submission for completeness and validity. You'll receive a
                unique reference ID to track your report's progress. Our team may contact you for
                additional information if needed.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-4">Investigation & Action</h3>
              <p className="text-gray-700">
                PCAB conducts appropriate investigations and coordinates with relevant agencies.
                Depending on the severity, actions may include warnings, license suspension,
                or referral to law enforcement. You'll be updated on the resolution status.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Reports Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-primary text-center mb-12">What You Can Report</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h3 className="font-semibold mb-2">Safety Violations</h3>
              <p className="text-sm text-gray-600">Unsafe work practices, missing safety equipment, hazardous conditions</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-blue-500 text-4xl mb-4">📋</div>
              <h3 className="font-semibold mb-2">Permit Issues</h3>
              <p className="text-sm text-gray-600">Missing permits, expired licenses, unauthorized construction</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-yellow-500 text-4xl mb-4">🏗️</div>
              <h3 className="font-semibold mb-2">Quality Problems</h3>
              <p className="text-sm text-gray-600">Poor workmanship, substandard materials, code violations</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-green-500 text-4xl mb-4">💰</div>
              <h3 className="font-semibold mb-2">Financial Irregularities</h3>
              <p className="text-sm text-gray-600">Overcharging, payment issues, contract disputes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Report Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary mb-8">Why Your Report Matters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Protect Public Safety</h3>
                <p className="text-gray-700">Help prevent accidents and ensure construction projects meet safety standards</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Maintain Quality Standards</h3>
                <p className="text-gray-700">Ensure contractors follow proper procedures and use quality materials</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Promote Accountability</h3>
                <p className="text-gray-700">Hold contractors responsible for their work and professional conduct</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Help Build a Better Philippines</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Your report can make a difference in ensuring quality construction and protecting communities across the Philippines.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/report" className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-3 rounded font-semibold">
              Submit a Report Now
            </Link>
            <Link to="/guidelines" className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-3 rounded transition-colors">
              Reporting Guidelines
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold mb-4">Need Help or Have Questions?</h3>
          <p className="text-gray-700 mb-4">
            Contact PCAB for assistance with your report or general inquiries about contractor accreditation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
            <span>📧 Email: reports@pcab.gov.ph</span>
            <span>📞 Hotline: (02) 8123-4567</span>
            <span>🕒 Mon-Fri: 8:00 AM - 5:00 PM</span>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
